import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import { type FilterDefinition } from '@oysterjs/ui/search-filter';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseSharedWithMeFilter,
  BaseTeamFilter,
  BaseUserFilter,
  BaseVisibilityFilter,
  type FilterViewer,
  type VisibilityFilterable,
  getSinceDate,
  matchesVisibilityFilter
} from '~/api/data/filter-base';

export type StyleFilter = 'search' | 'user' | 'date' | 'visibility' | 'sharedWithMe' | 'team';

/** HTML and Word library templates share these fields for search/user/date filters. */
export type StyleFilterableTemplate = VisibilityFilterable & {
  title: string;
  createdBy: { id?: string; name: string };
  updatedAt: Date | string | null | undefined;
};

function templateMatchesFilter(
  template: StyleFilterableTemplate,
  filter: ActiveFilter<StyleFilter>,
  viewer: FilterViewer
): boolean {
  const visibilityMatch = matchesVisibilityFilter(template, filter, viewer);
  if (visibilityMatch !== null) return visibilityMatch;
  switch (filter.type) {
    case 'search': {
      const term = (filter.value as string).toLowerCase();
      return (
        template.title.toLowerCase().includes(term) ||
        template.createdBy.name.toLowerCase().includes(term)
      );
    }
    case 'user':
      return (template.createdBy as { id?: string; name: string }).id === filter.value;
    case 'date': {
      const since = getSinceDate(filter.value as string);
      if (since == null) return true;
      return template.updatedAt != null && new Date(template.updatedAt) >= since;
    }
    default:
      return true;
  }
}

export function filterStyleTemplates<T extends StyleFilterableTemplate>(
  templates: T[],
  activeFilters: ActiveFilter<StyleFilter>[],
  viewer: FilterViewer
): T[] {
  if (activeFilters.length === 0) return templates;
  return templates.filter((t) => activeFilters.every((f) => templateMatchesFilter(t, f, viewer)));
}

export class StyleUserFilter extends BaseUserFilter<StyleFilter> {
  constructor() {
    super('user');
  }
}

export class StyleDateFilter extends BaseDateFilter<StyleFilter> {
  constructor() {
    super('date');
  }
}

export class StyleSearchFilter extends BaseSearchFilter<StyleFilter> {
  constructor() {
    super('search');
  }
}

export class StyleVisibilityFilter extends BaseVisibilityFilter<StyleFilter> {
  constructor() {
    super('visibility');
  }
}

export class StyleSharedWithMeFilter extends BaseSharedWithMeFilter<StyleFilter> {
  constructor() {
    super('sharedWithMe');
  }
}

export class StyleTeamFilter extends BaseTeamFilter<StyleFilter> {
  constructor() {
    super('team');
  }
}

export const createStyleFilterDefinitions = (): FilterDefinition<StyleFilter>[] => [
  new StyleUserFilter(),
  new StyleDateFilter(),
  new StyleSearchFilter(),
  new StyleVisibilityFilter(),
  new StyleSharedWithMeFilter(),
  new StyleTeamFilter()
];
